import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, ScrollText, X, Sparkles, ArrowRight, BarChart3 } from "lucide-react";
import { sr, actAs } from "../../lib/sr";
import ProposalAnalytics from "../../components/smartreach/ProposalAnalytics";

const STAGE_LABEL = {
  draft: "Draft", ready_for_review: "Ready for review", shared: "Shared",
  negotiating: "Negotiating", accepted: "Accepted", declined: "Declined", expired: "Expired",
};
const STAGE_TONE = {
  draft: "#64748b", ready_for_review: "#2563eb", shared: "#7c3aed",
  negotiating: "#d97706", accepted: "#16a34a", declined: "#b42318", expired: "#64748b",
};

function StageBadge({ stage }) {
  return (
    <span data-testid={`proposal-stage-${stage}`} style={{ fontSize: 11, fontWeight: 700, color: "#fff",
      background: STAGE_TONE[stage] || "#64748b", padding: "2px 9px", borderRadius: 999 }}>
      {STAGE_LABEL[stage] || stage}
    </span>
  );
}

function CreateModal({ industries, onClose, onCreated, onProspectCreated }) {
  const [mode, setMode] = useState("existing");
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ name: "", website: "", industry: "luxury" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { sr.proposalClients().then((r) => setClients(r.clients)).catch(() => setClients([])); }, []);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const createExisting = async () => {
    if (!sel) { toast.error("Pick a client to continue"); return; }
    setBusy(true);
    try { const r = await sr.createProposal({ tenant_id: sel }); toast.success("Proposal created from the client's saved info"); onCreated(r.proposal); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create proposal"); } finally { setBusy(false); }
  };
  const createNew = async () => {
    if (!f.name.trim()) { toast.error("Business name is required"); return; }
    setBusy(true);
    try {
      const r = await sr.createProposal({ name: f.name.trim(), website: f.website.trim() || null, industry: f.industry });
      onProspectCreated(r.proposal);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create prospect"); } finally { setBusy(false); }
  };
  const field = { width: "100%", padding: "9px 11px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13.5 };
  const filtered = (clients || []).filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,18,38,.55)", zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="create-proposal-modal" style={{ background: "#fff", borderRadius: 16, maxWidth: 640, width: "100%", padding: 22, marginTop: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 19 }}>Create Proposal</h2>
          <button onClick={onClose} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="create-proposal-close"><X size={16} /></button>
        </div>
        <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 0 }}>Pick an existing client to reuse their Add Client answers &amp; AI plan — or add a new prospect and complete the shared Add Client journey. Never triggers billing or an ad account.</p>

        <div style={{ display: "flex", gap: 6, margin: "10px 0 16px", borderBottom: "1px solid var(--border)" }}>
          {[["existing", "Use an existing client"], ["new", "Add a new prospect"]].map(([m, lbl]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`cp-mode-${m}`}
              style={{ padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13.5, fontWeight: 700,
                color: mode === m ? "var(--navy)" : "var(--muted)", borderBottom: mode === m ? "2px solid var(--gold)" : "2px solid transparent", marginBottom: -1 }}>
              {lbl}
            </button>
          ))}
        </div>

        {mode === "existing" ? (
          <div>
            <input style={{ ...field, marginBottom: 10 }} placeholder="Search clients & prospects…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cp-client-search" />
            {clients === null ? (
              <div style={{ textAlign: "center", padding: 24 }}><Loader2 size={18} className="sr-spin" /></div>
            ) : filtered.length === 0 ? (
              <div className="sr-muted" style={{ fontSize: 13, padding: 16, textAlign: "center" }}>No clients yet — add a new prospect in the other tab.</div>
            ) : (
              <div style={{ display: "grid", gap: 8, maxHeight: 320, overflowY: "auto" }} data-testid="cp-client-list">
                {filtered.map((c) => (
                  <button key={c.id} onClick={() => setSel(c.id)} data-testid={`cp-client-${c.id}`}
                    style={{ textAlign: "left", padding: "10px 12px", borderRadius: 10, cursor: "pointer", background: "#fff",
                      border: sel === c.id ? "2px solid var(--gold)" : "1px solid var(--border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, color: "var(--navy)" }}>{c.name}</span>
                      <span className="sr-badge" style={{ fontSize: 10.5, background: c.lifecycle === "proposal" ? "#FEF3C7" : "#E0F2FE", color: c.lifecycle === "proposal" ? "#92400E" : "#075985" }}>{c.lifecycle === "proposal" ? "Prospect" : "Client"}</span>
                    </div>
                    <div className="sr-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                      {c.industry_label}{c.website ? ` · ${c.website}` : ""}
                      {" · "}{c.onboarding_completed ? "Add Client ✓" : "Add Client pending"}
                      {c.growth_plan ? ` · AI plan v${c.growth_plan.version}` : " · no AI plan yet"}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button className="sr-btn sr-btn-gold" onClick={createExisting} disabled={busy || !sel} data-testid="cp-create-existing" style={{ marginTop: 16, width: "100%" }}>
              {busy ? <Loader2 size={15} className="sr-spin" /> : <ArrowRight size={15} />} Create proposal from this client
            </button>
          </div>
        ) : (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={{ gridColumn: "1 / -1", fontSize: 12, fontWeight: 600 }}>Business name *
                <input style={field} value={f.name} onChange={(e) => up("name", e.target.value)} placeholder="Rado" data-testid="cp-name" />
              </label>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Website
                <input style={field} value={f.website} onChange={(e) => up("website", e.target.value)} placeholder="rado.com" data-testid="cp-website" />
              </label>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Industry
                <select style={field} value={f.industry} onChange={(e) => up("industry", e.target.value)} data-testid="cp-industry">
                  {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
                </select>
              </label>
            </div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 12 }}>We'll create the prospect, then open the <strong>Add Client journey</strong> to capture goals, geography, audiences and budget once — the proposal reuses all of it.</p>
            <button className="sr-btn sr-btn-gold" onClick={createNew} disabled={busy} data-testid="cp-create-new" style={{ marginTop: 6, width: "100%" }}>
              {busy ? <Loader2 size={15} className="sr-spin" /> : <Plus size={15} />} Create prospect &amp; start Add Client
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProposalStudio() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [filter, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState("list");

  const load = () => sr.proposals(filter || undefined).then(setData).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load proposals"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { sr.proposalIndustries().then((r) => setIndustries(r.industries)).catch(() => {}); }, []);

  const rows = data?.proposals || [];
  return (
    <div data-testid="proposal-studio-page" style={{ padding: "22px 26px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, display: "flex", alignItems: "center", gap: 10 }}><ScrollText size={24} /> Proposal Studio</h1>
          <p className="sr-muted" style={{ marginTop: 4, fontSize: 13 }}>Create prospects, recommend targeting &amp; media plans, and generate branded proposals — no billing until they become a client.</p>
        </div>
        <button className="sr-btn sr-btn-gold" onClick={() => setShowCreate(true)} data-testid="open-create-proposal"><Plus size={15} /> Create Prospect &amp; Proposal</button>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 16, borderBottom: "1px solid var(--border)" }}>
        {[["list", "Proposals", ScrollText], ["analytics", "Analytics", BarChart3]].map(([v, lbl, Icon]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`proposal-view-${v}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", border: "none", background: "none", cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: view === v ? "var(--navy)" : "var(--muted)",
              borderBottom: view === v ? "2px solid var(--gold)" : "2px solid transparent", marginBottom: -1 }}>
            <Icon size={15} /> {lbl}
          </button>
        ))}
      </div>

      {view === "analytics" ? (
        <div style={{ marginTop: 18 }}><ProposalAnalytics /></div>
      ) : (
      <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "18px 0" }}>
        <button onClick={() => setFilter("")} className={`sr-btn sr-btn-sm ${filter === "" ? "sr-btn-navy" : "sr-btn-ghost"}`} data-testid="stage-filter-all">All ({rows.length && !filter ? rows.length : (data ? Object.values(data.stage_counts || {}).reduce((a, b) => a + b, 0) : 0)})</button>
        {(data?.stages || []).map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`sr-btn sr-btn-sm ${filter === s ? "sr-btn-navy" : "sr-btn-ghost"}`} data-testid={`stage-filter-${s}`}>
            {STAGE_LABEL[s]} {data?.stage_counts?.[s] ? `(${data.stage_counts[s]})` : ""}
          </button>
        ))}
      </div>

      {!data ? (
        <div style={{ textAlign: "center", padding: 60 }}><Loader2 className="sr-spin" size={26} /></div>
      ) : rows.length === 0 ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 46 }} data-testid="proposals-empty">
          <Sparkles size={30} style={{ color: "var(--gold)" }} />
          <h3 style={{ margin: "10px 0 4px" }}>No proposals yet</h3>
          <p className="sr-muted" style={{ fontSize: 13 }}>Start by creating a prospect — e.g. a luxury brand like Rado — and build a targeting &amp; media proposal.</p>
          <button className="sr-btn sr-btn-gold" onClick={() => setShowCreate(true)} style={{ marginTop: 10 }} data-testid="empty-create-proposal"><Plus size={15} /> Create Prospect &amp; Proposal</button>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((p) => (
            <button key={p.id} onClick={() => nav(`/app/proposals/${p.id}`)} data-testid={`proposal-row-${p.id}`} className="sr-card"
              style={{ textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", border: "1px solid var(--border)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <strong style={{ fontSize: 15 }}>{p.brand}</strong>
                  <StageBadge stage={p.stage} />
                  {p.industry_label && <span className="sr-muted" style={{ fontSize: 11.5 }}>{p.industry_label}</span>}
                </div>
                {p.objective && <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 3 }}>{p.objective}</div>}
                <div className="sr-muted" style={{ fontSize: 11, marginTop: 3 }}>
                  {p.versions} version{p.versions === 1 ? "" : "s"} · updated {new Date(p.updated_at).toLocaleDateString()}
                  {p.shared_at ? ` · shared ${new Date(p.shared_at).toLocaleDateString()}` : ""}
                </div>
              </div>
              <ArrowRight size={18} className="sr-muted" />
            </button>
          ))}
        </div>
      )}
      </>
      )}

      {showCreate && <CreateModal industries={industries} onClose={() => setShowCreate(false)} onCreated={(p) => { setShowCreate(false); nav(`/app/proposals/${p.id}`); }} onProspectCreated={(p) => { setShowCreate(false); actAs.set(p.tenant_id, (p.prospect && p.prospect.name) || p.brand); toast.success("Prospect created — complete the Add Client journey, then return to Proposal Studio"); nav("/app/onboarding"); }} />}
    </div>
  );
}
